import React, { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import NodeWrapper from "./NodeWrapper";
import useHandleConnected from "./useHandleConnected";
import { SmartToy } from "@mui/icons-material";

/**
 * AI Agent Node — Agente autonomo com ferramentas.
 *
 * data.provider: "openai" | "gemini" | "claude" — provedor de IA
 * data.model: string — modelo (ex: "gpt-4o", "gemini-pro", "claude-3-sonnet")
 * data.systemPrompt: string — prompt do sistema
 * data.tools: array — ferramentas disponiveis para o agente
 * data.maxSteps: number — maximo de passos autonomos (default 5)
 * data.outputVar: string — variavel para a resposta final
 * data.temperature: number — temperatura (0-2)
 *
 * Saidas: handle "success" (concluiu), handle "error" (falhou)
 */
const AIAgentNode = memo(({ id, data, isConnectable, selected }) => {
  const getHandleClass = useHandleConnected(id);
  const provider = data?.provider || "openai";
  const model = data?.model || "gpt-4o";
  const maxSteps = data?.maxSteps || 5;
  const toolCount = data?.tools?.length || 0;

  const providerColors = {
    openai: "#10A37F",
    gemini: "#4285F4",
    claude: "#CC785C",
  };

  return (
    <NodeWrapper
      id={id}
      data={data}
      icon={<SmartToy />}
      title="Agente IA"
      color={providerColors[provider] || "#10A37F"}
      bgColor="#E8F5E9"
      noSource
      isConnectable={isConnectable}
      selected={selected}
      minWidth={220}
    >
      <div style={{
        background: "#fff", border: "1px solid #e0e0e0",
        borderRadius: 6, padding: "5px 10px", fontSize: 11,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#fff",
            background: providerColors[provider] || "#10A37F",
            borderRadius: 4, padding: "1px 8px",
          }}>{provider.toUpperCase()}</span>
          <span style={{ color: "#666", fontFamily: "'Fira Code', monospace", fontSize: 10 }}>{model}</span>
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: 10, color: "#999" }}>
          <span>🛠 {toolCount} ferramentas</span>
          <span>📊 max {maxSteps} passos</span>
        </div>
      </div>

      {data?.systemPrompt && (
        <div style={{
          fontSize: 10, color: "#666", marginTop: 4,
          maxHeight: 32, overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {data.systemPrompt.substring(0, 50)}{data.systemPrompt.length > 50 ? "..." : ""}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: 6, position: "relative", paddingRight: 4,
        }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#fff",
            background: "#4caf50", borderRadius: 4, padding: "2px 10px",
          }}>SUCESSO</span>
          <Handle type="source" position={Position.Right} id="success"
            className={getHandleClass("source", "success")}
            style={{
              position: "absolute", top: "50%", right: -13,
              transform: "translateY(-50%)",
              cursor: "crosshair",
            }} isConnectable={isConnectable} />
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: 6, position: "relative", paddingRight: 4,
        }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#fff",
            background: "#f44336", borderRadius: 4, padding: "2px 10px",
          }}>ERRO</span>
          <Handle type="source" position={Position.Right} id="error"
            className={getHandleClass("source", "error")}
            style={{
              position: "absolute", top: "50%", right: -13,
              transform: "translateY(-50%)",
              cursor: "crosshair",
            }} isConnectable={isConnectable} />
        </div>
      </div>
    </NodeWrapper>
  );
});

AIAgentNode.displayName = "AIAgentNode";
export default AIAgentNode;
